import { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth, passkeySupported } from "./auth/AuthProvider";
import { SignIn, RegisterDevicePrompt } from "./auth/SignIn";
import { LinkProblem, SetUpSignIn } from "./auth/SetUpSignIn";
import { BrandLoading } from "./brand/Brand";
import { AppShell } from "./shell/AppShell";
import { ToastProvider } from "./ui/toast";
import { Loading } from "./ui/primitives";
import { routes } from "./routes";
import { isOnline } from "./offline/useOnline";
import { startAutoSync } from "./offline/sync";
import { SyncNotices } from "./offline/SyncNotices";
import { NotKeptNotice } from "./offline/NotKeptNotice";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: (n) => n < 1 && isOnline(), refetchOnWindowFocus: true, networkMode: "always" } }
});

function NotLinked() {
  const { signOut } = useAuth();
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 text-center">
      <h1 className="text-[26px]">Your login is not linked yet</h1>
      <p className="mt-2 text-muted">Ask the CEO to link your login to your name in Settings, People.</p>
      <button type="button" className="mt-6 min-h-[44px] text-primary" onClick={() => void signOut()}>Sign out</button>
    </main>
  );
}

function Gate() {
  const { ready, session, me, offline, linkProblem, setUpPending } = useAuth();
  useEffect(() => (session || offline ? startAutoSync() : undefined), [session, offline]);
  const [skippedRegister, setSkippedRegister] = useState(() => sessionStorage.getItem("rk-skip-register") === "1");
  if (!ready) return <BrandLoading label="Starting" />;
  if (linkProblem && !session) return <LinkProblem />;
  if (!session && !(offline && me)) return <SignIn />;
  if (!me) return <NotLinked />;
  if (setUpPending && !offline) return <SetUpSignIn />;
  // Also skipped when "Set up your sign-in" was told this device has no Face ID or fingerprint.
  const skipped = skippedRegister || sessionStorage.getItem("rk-skip-register") === "1";
  if (!offline && !me.has_passkey && passkeySupported() && !skipped) return <RegisterDevicePrompt onDone={() => { sessionStorage.setItem("rk-skip-register", "1"); setSkippedRegister(true); }} />;
  return (
    <Routes>
      <Route element={<><SyncNotices /><NotKeptNotice /><AppShell /></>}>
        <Route index element={<Navigate to="/today" replace />} />
        {routes.map((r) => (
          <Route key={r.path} path={r.path} element={<Suspense fallback={<Loading />}><r.Component /></Suspense>} />
        ))}
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <Gate />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
